import { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from '@heroui/react';
import type { BodegaRequest } from '@/types/bodega.model';

interface BodegaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BodegaRequest['data']) => Promise<void>;
  loading?: boolean;
}

const BodegaModal = ({ isOpen, onClose, onSubmit, loading }: BodegaModalProps) => {
  const [formData, setFormData] = useState<Omit<BodegaRequest['data'], 'tenantId' | 'locale' | 'localizations' | 'foto'>>({
    codigo: '',
    nombre: '',
    estado: 'Activa',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    const newErrors: Record<string, string> = {}
    if (!formData.codigo.trim()) newErrors.codigo = 'Código es requerido';
    if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
      // Clear form data after successful submission
      setFormData({
        codigo: '',
        nombre: '',
        estado: 'Activa',
      });
      // Clear any existing errors
      setErrors({});
      // Close the modal after successful submission
      onClose();
    } catch (error) {
      // Optionally handle submission errors here
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.name, e.target.value);
  };

  const handleSelectionChange = (e: any) => {
    handleChange('estado', e.currentKey.toString());
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      placement="top-center"
      backdrop="blur"
      size="2xl"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Crear Nueva Bodega</ModalHeader>
            <ModalBody className="space-y-4">
                <hr/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Código"
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleInputChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.codigo}
                />
                
                <Input
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.nombre}
                />
                
                <Select                  
                  className="max-w-xs"
                  selectedKeys={[formData.estado || 'Activa']}
                  label="Estado"
                  variant="bordered"    
                  placeholder="Seleccione un estado"
                  onSelectionChange={handleSelectionChange}
                >
                  <SelectItem key="Activa">Activa</SelectItem>
                  <SelectItem key="Inactiva">Inactiva</SelectItem>
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                color="primary" 
                onPress={handleSubmit}
                isLoading={loading}
              >
                Guardar Bodega
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default BodegaModal;